import React, { useEffect, useState, useRef, lazy } from 'react';
import { FadeIn } from './ui/FadeIn';
import { ArrowRightIcon, ShieldCheckIcon, DownloadIcon, InfinityIcon, ShieldIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, GiftIcon, ClockIcon } from 'lucide-react';
import { carouselSlides, iconMap } from './BonusSection';
import type { Slide } from './BonusSection';
export function PricingSection() {
  const [showBonuses, setShowBonuses] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  // Timer state
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };
  useEffect(() => {
    if (showBonuses) {
      setTimeout(checkScroll, 100);
      const el = scrollRef.current;
      if (el) el.addEventListener('scroll', checkScroll, {
        passive: true
      });
      return () => {
        if (el) el.removeEventListener('scroll', checkScroll);
      };
    }
  }, [showBonuses]);
  // Timer effect
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timerId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [timeLeft]);
  const formatTime = (time: number) => {
    const m = Math.floor(time / 60).toString().padStart(2, '0');
    const s = (time % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };
  const isUrgent = timeLeft < 5 * 60;
  const progressPercent = timeLeft / (15 * 60) * 100;
  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-slide]')?.clientWidth ?? 180;
    el.scrollBy({
      left: direction === 'left' ? -(cardWidth + 12) : cardWidth + 12,
      behavior: 'smooth'
    });
  };
  return <section id="pricing" className="py-10 px-6 lg:px-8 bg-tea-green text-tea-cream relative overflow-hidden" data-id="element-228">
      {/* Decorative background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-full max-h-[800px] bg-tea-gold/10 rounded-full blur-3xl pointer-events-none" data-id="element-229"></div>

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-230">
        <FadeIn data-id="element-231">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl text-center border-4 border-tea-gold/20" data-id="element-232">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-tea-green mb-6" data-id="element-233">
              Accede hoy al Plan de Escape del Té
            </h2>

            <div className="mb-8 max-w-2xl mx-auto" data-id="element-234">
              <p className="text-lg text-tea-charcoal/80 font-medium mb-4" data-id="element-235">
                Todo lo que necesitas para lanzar tu marca, en un solo paquete:
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-sm font-bold mb-5" data-id="element-236">
                <span className="bg-tea-green/10 text-tea-green px-4 py-2 rounded-full border border-tea-green/20" data-id="element-237">
                  📖 Libro Maestro completo
                </span>
                <span className="bg-tea-gold/10 text-tea-gold px-4 py-2 rounded-full border border-tea-gold/20" data-id="element-238">
                  🎁 19 Bonus de implementación
                </span>
                <span className="bg-violet-50 text-violet-700 px-4 py-2 rounded-full border border-violet-200" data-id="element-239">
                  🍵 250 Recetas de autor
                </span>
              </div>
              <p className="text-base text-tea-charcoal/60 leading-relaxed" data-id="element-240">
                Este es el precio de{' '}
                <span className="font-bold text-tea-green" data-id="element-241">lanzamiento</span>.
                Una vez que termine esta etapa, el precio subirá a su valor real
                de mercado.
                <span className="font-bold text-tea-charcoal/80" data-id="element-242">
                  {' '}
                  Hoy puedes asegurar tu acceso completo al mejor precio.
                </span>
              </p>
            </div>

            {/* Ver los 19 bonus toggle */}
            <div className="mb-10" data-id="element-243">
              <button onClick={() => setShowBonuses(!showBonuses)} className="inline-flex items-center gap-2 text-tea-gold font-bold text-sm hover:text-tea-green transition-colors group" data-id="element-244">
                <GiftIcon className="w-4 h-4" data-id="element-245" />
                <span className="underline underline-offset-2" data-id="element-246">
                  Ver los 19 bonus incluidos
                </span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${showBonuses ? 'rotate-180' : ''}`} data-id="element-247" />
              </button>

              {/* Collapsible bonus carousel */}
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${showBonuses ? 'max-h-[800px] opacity-100 mt-6' : 'max-h-0 opacity-0 mt-0'}`} data-id="element-248">
                <div className="relative" data-id="element-249">
                  <div className="flex items-center justify-between mb-4" data-id="element-250">
                    <p className="text-sm font-bold tracking-wider text-tea-charcoal/50 uppercase" data-id="element-251">
                      19 herramientas incluidas gratis
                    </p>
                    <div className="hidden md:flex items-center gap-1" data-id="element-252">
                      <button onClick={() => scroll('left')} disabled={!canScrollLeft} className="w-8 h-8 rounded-full border border-tea-gold/30 flex items-center justify-center text-tea-gold transition-all hover:bg-tea-gold/10 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Anterior" data-id="element-253">
                        <ChevronLeftIcon className="w-4 h-4" data-id="element-254" />
                      </button>
                      <button onClick={() => scroll('right')} disabled={!canScrollRight} className="w-8 h-8 rounded-full border border-tea-gold/30 flex items-center justify-center text-tea-gold transition-all hover:bg-tea-gold/10 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Siguiente" data-id="element-255">
                        <ChevronRightIcon className="w-4 h-4" data-id="element-256" />
                      </button>
                    </div>
                  </div>

                  <div ref={scrollRef} className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2" style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }} data-id="element-257">
                    {carouselSlides.map((slide, idx) => {
                    if (slide.type === 'category') {
                      const Icon = iconMap[slide.icon];
                      return <div key={`cat-${idx}`} data-slide className="relative flex-shrink-0 w-[48vw] sm:w-[35vw] md:w-[170px] snap-start" data-id="element-258">
                            <div className={`rounded-xl ${slide.bgColor} ${slide.borderColor} border-2 flex flex-col items-center justify-center p-4 text-center`} style={{
                          minHeight: '280px'
                        }} data-id="element-259">
                              <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center mb-3 shadow-sm" data-id="element-260">
                                <Icon className={`w-5 h-5 ${slide.color}`} data-id="element-261" />
                              </div>
                              <h4 className={`font-heading font-bold text-sm mb-2 leading-tight ${slide.color}`} data-id="element-262">
                                {slide.name}
                              </h4>
                              <p className={`text-xs leading-snug opacity-60 ${slide.color}`} data-id="element-263">
                                {slide.tagline}
                              </p>
                            </div>
                          </div>;
                    } else {
                      return <div key={`bonus-${idx}`} data-slide className="relative flex-shrink-0 w-[48vw] sm:w-[35vw] md:w-[170px] snap-start" data-id="element-264">
                            <div className="rounded-xl bg-tea-cream/50 border border-tea-sage/30 overflow-hidden" style={{
                          minHeight: '280px'
                        }} data-id="element-265">
                              <div className="aspect-[4/3] overflow-hidden relative" data-id="element-266">
                                <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" loading="lazy" data-id="element-267" />
                                <div className={`absolute top-1.5 right-1.5 ${slide.categoryColor} text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full`} data-id="element-268">
                                  GRATIS
                                </div>
                              </div>
                              <div className="p-2.5 text-left" data-id="element-269">
                                <p className="text-tea-charcoal/80 text-xs font-bold leading-snug mb-1 line-clamp-1" data-id="element-270">
                                  {slide.title}
                                </p>
                                <p className="text-tea-charcoal/60 text-[11px] leading-snug mb-1.5 line-clamp-2" data-id="element-271">
                                  {slide.desc}
                                </p>
                                <div className="flex items-center gap-1.5" data-id="element-272">
                                  <span className="text-red-500 line-through text-xs font-bold" data-id="element-273">
                                    {slide.price}
                                  </span>
                                  <span className="text-tea-green font-black text-xs uppercase" data-id="element-274">
                                    Gratis
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>;
                    }
                  })}
                  </div>

                  {/* Value stack summary */}
                  <div className="mt-5 bg-tea-green/5 border border-tea-green/20 rounded-xl p-4 text-center" data-id="element-275">
                    <p className="text-tea-charcoal/60 text-sm mb-2" data-id="element-276">
                      Valor total de los 19 bonus:
                    </p>
                    <div className="flex items-center justify-center gap-3" data-id="element-277">
                      <span className="text-red-500 line-through text-2xl font-heading font-bold" data-id="element-278">
                        $412 USD
                      </span>
                      <span className="text-tea-green font-heading font-black text-2xl" data-id="element-279">
                        ¡GRATIS HOY!
                      </span>
                    </div>
                    <p className="text-tea-charcoal/40 text-xs mt-2 italic" data-id="element-280">
                      Incluidos sin costo adicional con tu compra
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Timer Block */}
            <div className={`max-w-sm mx-auto mb-6 p-4 rounded-xl border ${isUrgent ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} transition-colors duration-500`} data-id="element-281">
              <div className="flex items-center justify-center gap-2 mb-1" data-id="element-282">
                <ClockIcon className={`w-5 h-5 ${isUrgent ? 'text-red-500 animate-pulse' : 'text-amber-600'}`} data-id="element-283" />
                <span className={`font-bold text-sm tracking-wider uppercase ${isUrgent ? 'text-red-600' : 'text-amber-700'}`} data-id="element-284">
                  Oferta expira en:
                </span>
              </div>
              <div className={`text-4xl font-heading font-bold tabular-nums ${isUrgent ? 'text-red-600 animate-pulse' : 'text-amber-600'}`} data-id="element-285">
                {formatTime(timeLeft)}
              </div>
            </div>

            <div className="mb-10" data-id="element-286">
              <span className="block text-tea-charcoal/50 uppercase tracking-widest text-sm font-bold mb-2" data-id="element-287">
                Único pago de:
              </span>
              <div className="flex flex-col items-center justify-center" data-id="element-288">
                <span className="text-xl md:text-3xl font-bold text-red-500/70 line-through mb-1" data-id="element-289">
                  $49.99 USD
                </span>
                <div className="flex items-baseline" data-id="element-290">
                  <span className="text-5xl md:text-7xl font-heading font-bold text-tea-gold" data-id="element-291">
                    $16.99
                  </span>
                  <span className="text-xl md:text-2xl text-tea-charcoal/60 ml-2 font-bold" data-id="element-292">
                    USD
                  </span>
                </div>
                <div className="mt-3 inline-flex items-center px-4 py-1.5 rounded-full bg-green-100 text-green-700 font-bold text-sm border border-green-200 shadow-sm" data-id="element-293">
                  Ahorras $33 USD
                </div>
              </div>
            </div>

            <a href="https://raicesdivinas.com/cart/add?id=53371756773742&quantity=1" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-white bg-tea-green hover:bg-tea-green/90 transition-all duration-300 rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-1 w-full sm:w-auto group mb-8 border border-tea-green/50" data-id="element-294">
              QUIERO COMENZAR MI NEGOCIO AHORA
              <ArrowRightIcon className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-295" />
            </a>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-tea-charcoal/60 text-sm max-w-2xl mx-auto" data-id="element-296">
              <div className="flex items-center justify-center bg-tea-cream/50 py-2 px-4 rounded-lg" data-id="element-297">
                <ShieldCheckIcon className="w-5 h-5 text-tea-gold mr-2" data-id="element-298" />
                <span data-id="element-299">Acceso inmediato</span>
              </div>
              <div className="flex items-center justify-center bg-tea-cream/50 py-2 px-4 rounded-lg" data-id="element-300">
                <DownloadIcon className="w-5 h-5 text-tea-gold mr-2" data-id="element-301" />
                <span data-id="element-302">100% Digital y descargable</span>
              </div>
              <div className="flex items-center justify-center bg-tea-cream/50 py-2 px-4 rounded-lg" data-id="element-303">
                <InfinityIcon className="w-5 h-5 text-tea-gold mr-2" data-id="element-304" />
                <span data-id="element-305">Acceso de por vida</span>
              </div>
            </div>

            <p className="mt-6 text-xs text-tea-charcoal/40 italic" data-id="element-306">
              Verás la conversión a tu moneda luego de hacer clic en el botón.
            </p>
          </div>
        </FadeIn>

        {/* 7-Day Guarantee */}
        <FadeIn delay={0.3} data-id="element-307">
          <div className="mt-10 flex flex-col items-center text-center" data-id="element-308">
            <div className="w-16 h-16 rounded-full bg-tea-gold/20 flex items-center justify-center mb-4" data-id="element-309">
              <ShieldIcon className="w-8 h-8 text-tea-gold" data-id="element-310" />
            </div>
            <h3 className="text-xl font-heading font-bold text-tea-gold mb-2" data-id="element-311">
              Garantía de 7 Días
            </h3>
            <p className="text-tea-cream/70 text-sm max-w-md leading-relaxed" data-id="element-312">
              Si dentro de los primeros 7 días sientes que este material no es
              para ti, te devolvemos el 100% de tu dinero. Sin preguntas, sin
              complicaciones.
            </p>
          </div>
        </FadeIn>
      </div>

      {/* Fixed Urgency Progress Bar */}
      <div className="fixed bottom-0 left-0 w-full z-50" data-id="element-313">
        <div className="w-full h-1.5 bg-gray-300/40" data-id="element-314">
          <div className={`h-full transition-all duration-1000 ease-linear ${isUrgent ? 'bg-red-500' : 'bg-tea-gold'}`} style={{
          width: `${progressPercent}%`
        }} data-id="element-315" />
        </div>
      </div>
    </section>;
}